import type { ChatMessage, UploadItem } from "../types/redux";
import { updateProjectMetadata } from "../ProjectSlice_Store";
import { addMessage, updateUploadInTable } from "./chatSlice";
import { sendUploadBatchComplete } from "./chatSendThunks";
import type { ChatDispatch } from "./chatSliceTypes";

type UploadMap = Record<string, UploadItem>;

/** The notification envelope the core agent puts on `channelData.notification`. */
export interface ChatNotification {
  type?: string;
  project_id?: string;
  filename?: string;
  processing_status?: string;
  error_message?: unknown;
  doc_id?: string;
  name?: string;
  summary?: string;
}

interface UploadStatusUpdate {
  statusText: string;
  completed: boolean;
  error: boolean;
}

const READY_STATUS = "Ready";

/** Redux is the single source of truth here; the listener has no closure state. */
const currentState = (): any => window.store?.getState();

const isForCurrentProject = (notification: ChatNotification): boolean => {
  const currentProjectId = currentState()?.projectSlice?.currentProjectId;
  return Boolean(notification.project_id && notification.project_id === currentProjectId);
};

/** Translate the backend's processing status into upload-table display state. */
export function mapProcessingStatus(
  processingStatus: string,
  errorMessage?: unknown
): UploadStatusUpdate {
  if (processingStatus === "processing") {
    return { statusText: "Processing document...", completed: false, error: false };
  }
  if (processingStatus === "processed") {
    return { statusText: READY_STATUS, completed: true, error: false };
  }
  if (processingStatus === "failed") {
    // Ensure error_message is a string (not [Object object])
    const statusText =
      typeof errorMessage === "string"
        ? errorMessage
        : errorMessage
          ? String(errorMessage)
          : "Processing failed";
    return { statusText, completed: true, error: true };
  }
  // Unknown status - default to waiting
  return { statusText: "Waiting for processing...", completed: false, error: false };
}

/** Locate the still-open upload table row for a filename. */
export function findOpenUploadId(messages: ChatMessage[], filename: string): string | undefined {
  const uploadTableMessage = messages.find(
    (msg: ChatMessage) => msg.type === "upload-table" && !msg.allCompleted
  );
  const uploads = uploadTableMessage?.uploads as UploadMap | undefined;
  if (!uploads) return undefined;

  const entry = Object.entries(uploads).find(([, upload]) => upload.fileName === filename);
  return entry?.[0];
}

const succeeded = (uploads: UploadItem[]): UploadItem[] =>
  uploads.filter((upload) => upload.completed && !upload.error);

/**
 * Once the final file finishes processing, persist the whole batch as a
 * conversation turn — guarded by a window flag so a re-delivered notification
 * can't send it twice.
 */
function maybeSendBatchComplete(uploadId: string, dispatch: ChatDispatch): void {
  const updatedMessages: ChatMessage[] = currentState()?.chatSlice?.messages || [];
  // Find the upload-table that contains this specific uploadId (not just any upload-table)
  const table = updatedMessages.find(
    (msg: ChatMessage) =>
      msg.type === "upload-table" && msg.uploads && (msg.uploads as UploadMap)[uploadId]
  );
  if (!table?.allCompleted) return;

  const uploads = Object.values(table.uploads as UploadMap);
  const allProcessed = succeeded(uploads).every((upload) => upload.status === READY_STATUS);

  const batchSentKey = `upload-batch-sent-${table.clientMessageId}`;
  const alreadySent = (window as any)[batchSentKey];
  if (!allProcessed || alreadySent) return;

  // Mark as sent to prevent duplicate sends
  (window as any)[batchSentKey] = true;

  const completedFiles = succeeded(uploads).map((upload) => ({
    fileName: upload.fileName,
    fileSize: upload.fileSize,
    docId: upload.docId,
  }));

  if (completedFiles.length > 0) {
    console.log("📦 [UPLOAD_BATCH] All files processed, sending batch complete", {
      fileCount: completedFiles.length,
      files: completedFiles.map((file) => file.fileName),
    });
    dispatch(sendUploadBatchComplete(completedFiles));
  }
}

/** Reflect an ingestion status change in the upload table rather than the transcript. */
export function handleFileProcessingUpdate(
  notification: ChatNotification,
  dispatch: ChatDispatch
): void {
  if (!isForCurrentProject(notification)) return;

  const filename = notification.filename;
  const processingStatus = notification.processing_status;
  if (!filename || !processingStatus) return;

  const uploadId = findOpenUploadId(currentState()?.chatSlice?.messages || [], filename);
  if (!uploadId) return;

  const { statusText, completed, error } = mapProcessingStatus(
    processingStatus,
    notification.error_message
  );

  dispatch(
    updateUploadInTable({
      uploadId,
      status: statusText,
      completed,
      error,
      docId: notification.doc_id, // Capture document ID from processing notification
    })
  );

  if (processingStatus === "processed") {
    maybeSendBatchComplete(uploadId, dispatch);
  }
}

/**
 * Route a control-channel notification. Returns true when the activity was a
 * notification and must not fall through to normal message handling.
 */
export function handleNotification(
  notification: ChatNotification | undefined,
  activityText: string,
  dispatch: ChatDispatch
): boolean {
  if (!notification) return false;

  if (notification.type === "file_processing_update") {
    handleFileProcessingUpdate(notification, dispatch);
    return true;
  }

  if (notification.type === "draft_document_update") {
    if (isForCurrentProject(notification)) {
      // Show notification message in chat
      dispatch(addMessage({ sender: "chatbot", text: activityText }));
    }
    return true;
  }

  if (notification.type === "project_checkpoint_update") {
    if (isForCurrentProject(notification)) {
      // Update project metadata in Redux store (no chat message shown)
      dispatch(
        updateProjectMetadata({ name: notification.name, summary: notification.summary })
      );
    }
    return true;
  }

  return false;
}
